import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

// One unified timeline entry as returned by leadAction.getLeadTimeline.
// Activities (calls / meetings / notes) come straight from client_activities.
export interface TimelineActivity {
  activityId: string;
  brokerId: string | null;
  brokerName: string | null;
  activityType: string;
  outcome: string | null;
  clientName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
  linkedLoanId: string | null;
  linkedLeadId: string | null;
  notes: string | null;
  durationSeconds: number | null;
  followUpAt: string | null;
  occurredAt: string | null;
  loggedAt: string | null;
}

export interface TimelineAssignmentEntry {
  brokerId: string | null;
  reason: string | null;
  assignedAt: string | null;
  cursorPosition: number | null;
  rotationRuleId: string | null;
}

export interface TimelineStatusEvent {
  action: string | null;
  actorUid: string | null;
  actorEmail: string | null;
  newStatus: string | null;
  previousAssignedBrokerId: string | null;
  newAssignedBrokerId: string | null;
  timestamp: string | null;
}

export interface LeadTimeline {
  activities: TimelineActivity[];
  assignmentHistory: TimelineAssignmentEntry[];
  // Admin-only — brokers always receive [].
  statusEvents: TimelineStatusEvent[];
  // Deprecated lead.notes single string, surfaced read-only (no migration).
  legacyNote: string | null;
}

interface UseLeadTimelineState {
  timeline: LeadTimeline | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const EMPTY: LeadTimeline = {
  activities: [],
  assignmentHistory: [],
  statusEvents: [],
  legacyNote: null,
};

/**
 * Fetches the lead detail drawer feed (activities + notes + assignment history
 * + admin status events) via leadAction.getLeadTimeline. The callable enforces
 * the same lead-visibility scope as updateLead — a broker reading a lead
 * reassigned away from them gets a permission-denied (surfaced via `error`).
 *
 * Pass `leadId = null` (drawer closed) to stay idle. Re-fetches on open and on
 * any explicit refresh() (after logging a contact / note / reassign).
 */
export function useLeadTimeline(
  companyId: string | undefined,
  leadId: string | null,
  // Bump this from the parent to force a re-fetch (e.g. after a Log-contact
  // modal submit that wrote a new activity to this lead).
  externalRefreshSignal?: number,
): UseLeadTimelineState {
  const [timeline, setTimeline] = useState<LeadTimeline | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId || !leadId) {
      setTimeline(null);
      setError(null);
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      {
        action: 'getLeadTimeline';
        companyId: string;
        leadId: string;
      },
      LeadTimeline
    >(functions, 'leadAction');

    let cancelled = false;
    setLoading(true);
    setError(null);
    fn({ action: 'getLeadTimeline', companyId, leadId })
      .then((r) => {
        if (cancelled) return;
        setTimeline({ ...EMPTY, ...r.data });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useLeadTimeline] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
        setTimeline(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, leadId, refreshKey, externalRefreshSignal]);

  return { timeline, loading, error, refresh };
}
